import { PermissionAccessProvider, PermissionAwareInput } from '../../../contexts/PermissionAccessContext';
import type { TicketDetail } from '../../../types/safetySystem';
import { DateField, Field, SelectField, Section, TextField, YesNoField, inputClass, str } from './unitFields';
import { inspectionMinutes, isClosed } from './ticketForm';
import { DriverTypeahead, driverLabel } from './ticketTypeaheads';

/**
 * Ticket Info tab — V1 ticket drawer rows 1-4 (editrecord_safety.pl ticket form).
 * Row 1: ticket #, officer, precinct, county, hwy, mile marker. Row 2: driver, date,
 * start/end time, inspection minutes, clean, level, DOT bonus date. Row 3: pro #, copies
 * received, sent date, total weight. Row 4: created / last changed / closed.
 */

export interface TicketInfoTabProps {
  detail: TicketDetail;
  ticket: Record<string, any>;
  onChange: (key: string, value: unknown) => void;
  driverText: string;
  onDriverText: (text: string) => void;
  onDriverPick: (driverrecno: number, label: string) => void;
  closed: boolean;
  onClosedChange: (closed: boolean) => void;
  totalWeight: number;
  canEdit: boolean;
  portalContainer: HTMLElement | null;
}

const LEVEL_OPTIONS = ['1', '2', '3'].map((v) => ({ value: v, label: v }));

const ReadOnly = ({ label, value, className }: { label: string; value: unknown; className?: string }) => (
  <Field label={label} className={className}>
    <input aria-label={label} className={inputClass} value={str(value)} readOnly disabled />
  </Field>
);

export default function TicketInfoTab({
  detail, ticket, onChange, driverText, onDriverText, onDriverPick, closed, onClosedChange, totalWeight, canEdit, portalContainer,
}: TicketInfoTabProps) {
  const t = (key: string) => (v: unknown) => onChange(key, v);
  const officers = (detail.lookups?.officers ?? []).map((o) => ({ value: String(o.recno), label: o.badgenum ? `${o.label} (${o.badgenum})` : o.label }));
  const minutes = inspectionMinutes(ticket.ticketstarttime, ticket.ticketendtime);
  const clean = str(ticket.ticketclean).toUpperCase();
  const wasClosed = isClosed(detail.ticket?.closedatetime);

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="space-y-3">
          <Section title="Ticket">
            <TextField label="Ticket #" value={ticket.ticketnum} onChange={t('ticketnum')} maxLength={30} />
            <SelectField label="Inspected By" value={ticket.inspectedby && Number(ticket.inspectedby) > 0 ? ticket.inspectedby : ''}
              onChange={(v) => onChange('inspectedby', v === '' ? 0 : v)} options={officers} search />
            <TextField label="Precinct" value={ticket.ticketprecinct} onChange={t('ticketprecinct')} />
            <TextField label="County" value={ticket.ticketcounty} onChange={t('ticketcounty')} />
            <TextField label="Hwy" value={ticket.tickethwy} onChange={t('tickethwy')} />
            <TextField label="Mile Marker" value={ticket.milemarker} onChange={t('milemarker')} />
            <TextField label="Incident #" value={ticket.incidentrecno && Number(ticket.incidentrecno) > 0 ? ticket.incidentrecno : ''}
              onChange={(v) => onChange('incidentrecno', v === '' ? 0 : v)} type="number" />
            <TextField label="Pro #" value={ticket.pronumber && Number(ticket.pronumber) > 0 ? ticket.pronumber : ''}
              onChange={(v) => onChange('pronumber', v === '' ? 0 : v)} type="number" />
          </Section>
        </div>

        <div className="space-y-3">
          <Section title="Driver & Inspection">
            <Field label="Driver" className="col-span-2">
              <DriverTypeahead ariaLabel="Driver" value={driverText} onChange={onDriverText}
                onPick={(r) => onDriverPick(Number(r.driverrecno), driverLabel(r))} />
            </Field>
            <DateField label="Ticket Date" value={ticket.ticketstartdate} onChange={t('ticketstartdate')} portalContainer={portalContainer} className="col-span-2" />
            <TextField label="Start Time" value={ticket.ticketstarttime} onChange={t('ticketstarttime')} type="time" />
            <TextField label="End Time" value={ticket.ticketendtime} onChange={t('ticketendtime')} type="time" />
            <ReadOnly label="Inspection Minutes" value={minutes === null ? '' : minutes} />
            <YesNoField label="Clean" value={ticket.ticketclean} onChange={t('ticketclean')} />
            <SelectField label="Level" value={ticket.ticketlevel} onChange={t('ticketlevel')} options={LEVEL_OPTIONS} />
            {/* V1: the DOT bonus date is only editable on a clean inspection. */}
            <DateField label="DOT Bonus Create Date" value={ticket.dotbonuscreatedate} onChange={t('dotbonuscreatedate')}
              portalContainer={portalContainer} readOnly={clean !== 'Y'} />
          </Section>
        </div>

        <div className="space-y-3">
          <Section title="Paperwork">
            <DateField label="State Copy Rcvd" value={ticket.statecopyrcvddate} onChange={t('statecopyrcvddate')} portalContainer={portalContainer} />
            <DateField label="Phys Copy Rcvd" value={ticket.physcopyrcvddate} onChange={t('physcopyrcvddate')} portalContainer={portalContainer} />
            <DateField label="Sent Date" value={ticket.ticketsentdate} onChange={t('ticketsentdate')} portalContainer={portalContainer} />
            <ReadOnly label="Total Weight" value={totalWeight} />
          </Section>
          <Section title="Record">
            <ReadOnly label="Created By" value={detail.ticket?.createdby} />
            <ReadOnly label="Created" value={detail.ticket?.createdatetime} />
            <ReadOnly label="Last Changed By" value={detail.ticket?.lastchangedby} />
            <ReadOnly label="Last Changed" value={detail.ticket?.lastchangedatetime} />
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <PermissionAwareInput type="checkbox" aria-label="Closed" checked={closed} className="h-4 w-4 rounded border-rule-strong"
                onChange={(e) => onClosedChange(e.target.checked)} />
              Closed
              {wasClosed && (
                <span className="text-xs text-muted">
                  {`by ${str(detail.ticket?.closedby)} on ${str(detail.ticket?.closedatetime)}`}
                </span>
              )}
            </label>
          </Section>
        </div>
      </div>
    </PermissionAccessProvider>
  );
}
